import React, { useCallback, useEffect, useRef, useState } from 'react';
import { userService } from '../../_services';

/*
 * Invoice Chaser Agentic Assistant — floating launcher + chat drawer.
 *
 * Mount on any AR page:  <ARAssistant organizationId={orgId} accountId={user?.id} />
 * Talks to the Flask agent via the existing Django proxy (userService.aiRequest,
 * methods ar-assistant-chat / -history / -approve-action / -reject-action).
 * The session id lives in sessionStorage so the conversation follows the user
 * across AR pages within a browser session.
 */

interface PendingAction {
    action_id: number;
    action_type: string;
    status: string;
    to?: string;
    subject?: string;
    preview?: string;
    total_due?: number;
    invoice_count?: number;
    customer_name?: string;
    note?: string;
}

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    actions?: PendingAction[];
}

interface ARAssistantProps {
    organizationId: string | null;
    accountId: string | number | null;
}

const SESSION_KEY = 'ar_assistant_session_id';

// The Django proxy camelCases response keys (djangorestframework-camel-case),
// so normalise either form coming back from the API.
const normalizeAction = (a: any): PendingAction => ({
    action_id: a.action_id ?? a.actionId,
    action_type: a.action_type ?? a.actionType,
    status: a.status,
    to: a.to,
    subject: a.subject,
    preview: a.preview,
    total_due: a.total_due ?? a.totalDue,
    invoice_count: a.invoice_count ?? a.invoiceCount,
    customer_name: a.customer_name ?? a.customerName,
    note: a.note,
});
const getPendingActions = (data: any): PendingAction[] =>
    (data?.pending_actions ?? data?.pendingActions ?? []).map(normalizeAction);

const styles: { [k: string]: React.CSSProperties } = {
    fab: {
        position: 'fixed', bottom: 24, right: 24, zIndex: 1050,
        width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
        background: '#0065FF', color: 'white',
        fontSize: 24, boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
    },
    drawer: {
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1060,
        width: 420, maxWidth: '100vw', background: '#fff', display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)', fontFamily: "'Poppins', Arial, sans-serif",
    },
    header: {
        padding: '14px 18px', background: '#0065FF',
        color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    body: { flex: 1, overflowY: 'auto', padding: 16, background: '#f6f8fa' },
    bubbleUser: {
        alignSelf: 'flex-end', background: '#0065FF', color: 'white', borderRadius: '14px 14px 2px 14px',
        padding: '9px 13px', margin: '4px 0', maxWidth: '85%', whiteSpace: 'pre-wrap', fontSize: 14,
    },
    bubbleAssistant: {
        alignSelf: 'flex-start', background: 'white', color: '#222', borderRadius: '14px 14px 14px 2px',
        padding: '9px 13px', margin: '4px 0', maxWidth: '90%', whiteSpace: 'pre-wrap', fontSize: 14,
        border: '1px solid #e4e8ee',
    },
    actionCard: {
        border: '1px solid #d0d7e2', borderRadius: 10, background: 'white',
        padding: 12, margin: '6px 0', fontSize: 13,
    },
    inputRow: { display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #e4e8ee', background: 'white' },
    input: {
        flex: 1, border: '1px solid #cfd6e0', borderRadius: 8, padding: '10px 12px',
        fontSize: 14, outline: 'none', resize: 'none', fontFamily: 'inherit',
    },
    sendBtn: {
        border: 'none', borderRadius: 8, background: '#0065FF', color: 'white',
        padding: '0 18px', cursor: 'pointer', fontSize: 14,
    },
    smallBtn: {
        border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
        fontSize: 13, marginRight: 8,
    },
};

const ARAssistant: React.FC<ARAssistantProps> = ({ organizationId, accountId }) => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [editingAction, setEditingAction] = useState<number | null>(null);
    const [editedBody, setEditedBody] = useState('');
    const bodyRef = useRef<HTMLDivElement>(null);

    const getSessionId = () => {
        let sid = sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            sid = ([1e7] as any + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: any) =>
                (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
            );
            sessionStorage.setItem(SESSION_KEY, sid as string);
        }
        return sid;
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }, 50);
    };

    // Restore history when the drawer first opens
    useEffect(() => {
        if (!open || !organizationId || messages.length > 0) return;
        (async () => {
            try {
                const resp = await userService.aiRequest({
                    method: 'ar-assistant-history',
                    data: { organization_id: organizationId, session_id: getSessionId() },
                }, true);
                if (resp.data?.messages?.length) {
                    setMessages(resp.data.messages);
                } else {
                    setMessages([{
                        role: 'assistant',
                        content: "Hi! I'm Chase, your Invoice Chaser assistant. I can help you set up your AI agent, answer questions about your outstanding invoices, or draft reminder emails for you to approve. What would you like to do?",
                    }]);
                }
                scrollToBottom();
            } catch (e) { /* history is best-effort */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, organizationId]);

    const send = useCallback(async () => {
        const text = input.trim();
        if (!text || busy || !organizationId || !accountId) return;
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: text }]);
        setBusy(true);
        scrollToBottom();
        try {
            const resp = await userService.aiRequest({
                method: 'ar-assistant-chat',
                data: {
                    organization_id: organizationId,
                    account_id: accountId,
                    session_id: getSessionId(),
                    message: text,
                    page: window.location.pathname,
                },
            }, true);
            if (resp.data?.error) {
                setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, something went wrong: ${resp.data.error}` }]);
            } else {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: resp.data?.reply || '(no reply)',
                    actions: getPendingActions(resp.data),
                }]);
            }
        } catch (e: any) {
            setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I could not reach the assistant. Please try again.' }]);
        } finally {
            setBusy(false);
            scrollToBottom();
        }
    }, [input, busy, organizationId, accountId]);

    const resolveAction = async (action: PendingAction, approve: boolean, body?: string) => {
        setBusy(true);
        try {
            const resp = await userService.aiRequest({
                method: approve ? 'ar-assistant-approve-action' : 'ar-assistant-reject-action',
                data: {
                    organization_id: organizationId,
                    action_id: action.action_id,
                    ...(approve && body ? { edited_body: body } : {}),
                },
            }, true);
            const outcome = resp.data?.error
                ? `Action failed: ${resp.data.error}`
                : (resp.data?.result || (approve ? 'Approved.' : 'Rejected.'));
            setMessages(prev => {
                const cleaned: ChatMessage[] = prev.map(m => ({
                    ...m,
                    actions: m.actions?.filter(a => a.action_id !== action.action_id),
                }));
                cleaned.push({ role: 'assistant', content: outcome });
                return cleaned;
            });
        } catch (e) {
            setMessages(prev => [...prev, { role: 'assistant', content: 'Could not process the action. Please try again.' }]);
        } finally {
            setEditingAction(null);
            setBusy(false);
            scrollToBottom();
        }
    };

    const stripHtml = (html: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.innerText;
    };
    const toParagraphHtml = (text: string) =>
        text.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');

    const renderAction = (action: PendingAction) => (
        <div key={action.action_id} style={styles.actionCard}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {action.action_type === 'send_reminder_email'
                    ? `📧 Draft reminder email — awaiting your approval`
                    : action.action_type === 'send_reminder_sms'
                        ? `📱 Draft reminder SMS — awaiting your approval`
                        : action.action_type === 'update_settings'
                            ? `⚙️ Proposed settings change — awaiting your approval`
                            : action.action_type === 'update_customer_overrides'
                                ? `👤 Proposed customer-specific settings — awaiting your approval`
                                : `⏯ ${action.action_type.replace('_', ' ')} — awaiting your approval`}
            </div>
            {action.subject && <div><strong>Subject:</strong> {action.subject}</div>}
            {action.to && <div><strong>To:</strong> {action.to}</div>}
            {action.customer_name && <div><strong>Customer:</strong> {action.customer_name}</div>}
            {editingAction === action.action_id ? (
                <textarea
                    style={{ ...styles.input, width: '100%', minHeight: 120, margin: '8px 0' }}
                    value={editedBody}
                    onChange={e => setEditedBody(e.target.value)}
                />
            ) : (
                action.preview && (
                    <div style={{ background: '#f6f8fa', borderRadius: 6, padding: 8, margin: '8px 0', whiteSpace: 'pre-wrap' }}>
                        {stripHtml(action.preview)}
                    </div>
                )
            )}
            <div style={{ marginTop: 8 }}>
                <button
                    style={{ ...styles.smallBtn, background: '#28a745', color: 'white' }}
                    disabled={busy}
                    onClick={() => resolveAction(action, true,
                        editingAction === action.action_id
                            ? (action.action_type === 'send_reminder_sms' ? editedBody : toParagraphHtml(editedBody))
                            : undefined)}
                >
                    Approve{action.action_type === 'send_reminder_email' ? ' & Send' : ''}
                </button>
                {(action.action_type === 'send_reminder_email' || action.action_type === 'send_reminder_sms') && editingAction !== action.action_id && (
                    <button
                        style={{ ...styles.smallBtn, background: '#e9ecef', color: '#333' }}
                        disabled={busy}
                        onClick={() => { setEditingAction(action.action_id); setEditedBody(stripHtml(action.preview || '')); }}
                    >
                        Edit
                    </button>
                )}
                <button
                    style={{ ...styles.smallBtn, background: '#dc3545', color: 'white' }}
                    disabled={busy}
                    onClick={() => resolveAction(action, false)}
                >
                    Reject
                </button>
            </div>
        </div>
    );

    if (!organizationId || !accountId) return null;

    return (
        <>
            {!open && (
                <button style={styles.fab} onClick={() => setOpen(true)} title="Chase — Invoice Chaser Assistant">
                    <img
                        src="/assets/images/chase.png"
                        alt="Chase"
                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).parentElement!.textContent = '💬'; }}
                    />
                </button>
            )}
            {open && (
                <div style={styles.drawer}>
                    <div style={styles.header}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img
                                src="/assets/images/chase.png"
                                alt=""
                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.6)' }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                            <div style={{ fontWeight: 600 }}>Chase — Invoice Chaser Assistant</div>
                        </div>
                        <button
                            style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}
                            onClick={() => setOpen(false)}
                        >
                            ✕
                        </button>
                    </div>
                    <div ref={bodyRef} style={{ ...styles.body, display: 'flex', flexDirection: 'column' }}>
                        {messages.map((m, i) => (
                            <React.Fragment key={i}>
                                <div style={m.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant}>{m.content}</div>
                                {m.actions?.map(renderAction)}
                            </React.Fragment>
                        ))}
                        {busy && <div style={{ ...styles.bubbleAssistant, color: '#888' }}>Thinking…</div>}
                    </div>
                    <div style={styles.inputRow}>
                        <textarea
                            style={styles.input}
                            rows={1}
                            placeholder="Ask about setup, invoices, or chasing…"
                            value={input}
                            disabled={busy}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                            }}
                        />
                        <button style={styles.sendBtn} disabled={busy || !input.trim()} onClick={send}>
                            Send
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ARAssistant;
